import { useCallback, useEffect, useRef, useState } from "react";

export type AsyncState<T> =
  | { status: "idle"; data: null; error: null }
  | { status: "loading"; data: T | null; error: null }
  | { status: "success"; data: T; error: null }
  | { status: "error"; data: T | null; error: unknown };

export interface UseAsyncResult<T> {
  state: AsyncState<T>;
  reload: () => void;
  lastSuccessAt: Date | null;
  /** Falha permanece durante nova tentativa, ate sucesso ou troca de chave. */
  lastError: unknown | null;
}

/**
 * Hook minimalista para chamadas assincronas que dependem de uma chave.
 *
 * - Cancela requisicoes antigas via AbortController quando a key muda.
 * - Mantem o ultimo `data` durante reloads para evitar flash de loading.
 * - Re-executa quando `key` muda ou `reload()` e chamado manualmente.
 * - **Reconcilia por valor**: quando o payload novo é estruturalmente igual
 *   ao anterior (caso comum no auto-refresh com gateway parado), mantém a
 *   referência antiga em `state.data`. Isso evita cascata de re-renders
 *   (useMemo invalidando, `<select>` aberto pelo operador colapsando, etc.).
 *   O `status` ainda transita para `success` para sinalizar fim do loading.
 */
export function useAsync<T>(
  key: string,
  fn: (signal: AbortSignal) => Promise<T>
): UseAsyncResult<T> {
  const [state, setState] = useState<AsyncState<T>>({ status: "idle", data: null, error: null });
  const [nonce, setNonce] = useState(0);
  const [lastSuccessAt, setLastSuccessAt] = useState<Date | null>(null);
  const [lastError, setLastError] = useState<unknown | null>(null);
  const [keyAnterior, setKeyAnterior] = useState(key);
  const fnRef = useRef(fn);
  const pending = useRef(false);
  const queued = useRef(false);
  fnRef.current = fn;

  // DADO PERTENCE A UMA CHAVE. Trocar de chave nao deixa o dado velho: deixa
  // o dado DE OUTRO. Preservar `data` so faz sentido no reload da MESMA
  // chave, que e o caso que a regra original queria cobrir (nao piscar
  // "carregando" a cada refetch de 5 s).
  //
  // Sem esta linha, trocar de workflow na barra lateral mantinha em tela os
  // dispositivos do workflow anterior — e havia um segundo efeito, pior:
  // `hasLoadedOnce` (= `data !== null`) ficava verdadeiro ANTES da primeira
  // resposta da chave nova. Como `useAutoRefresh` e o SSE sao ligados por ele,
  // o intervalo de 5 s comecava a rodar de imediato e cada tique chamava
  // `reload()`, abortando o fetch inicial em curso. Com resposta lenta a tela
  // nunca terminava de carregar: o mesmo travamento que aquela guarda existia
  // para evitar, reintroduzido pela porta dos fundos.
  //
  // O ajuste acontece DURANTE O RENDER (padrao recomendado do React para
  // estado derivado de prop que mudou), e nao num efeito, para nao existir um
  // quadro intermediario em que a tela ainda mostra o dado antigo sob a chave
  // nova.
  if (key !== keyAnterior) {
    setKeyAnterior(key);
    setLastSuccessAt(null);
    setLastError(null);
    setState({ status: "loading", data: null, error: null });
  }

  useEffect(() => {
    const ctrl = new AbortController();
    let cancelled = false;
    pending.current = true;

    setState((prev) => ({
      status: "loading",
      data: prev.data,
      error: null,
    }));

    fnRef
      .current(ctrl.signal)
      .then((data) => {
        if (cancelled) return;
        // Consulta bem-sucedida nao exige um payload diferente.
        setLastSuccessAt(new Date());
        setLastError(null);
        setState((prev) => {
          // Reconciliação: durante um reload, prev.status é "loading" mas
          // prev.data ainda guarda o snapshot anterior. Compara contra
          // ele para preservar referências quando o conteúdo é idêntico.
          if (prev.data !== null && structurallyEqual(prev.data, data)) {
            return { status: "success", data: prev.data, error: null };
          }
          return { status: "success", data, error: null };
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if ((err as { name?: string })?.name === "AbortError") return;
        setLastError(err);
        setState((prev) => ({ status: "error", data: prev.data, error: err }));
      })
      .finally(() => {
        if (cancelled) return;
        pending.current = false;
        if (queued.current) {
          queued.current = false;
          setNonce((n) => n + 1);
        }
      });

    return () => {
      cancelled = true;
      pending.current = false;
      queued.current = false;
      ctrl.abort();
    };
  }, [key, nonce]);

  // SSE/poll/foco podem chegar mais rapido que a rede. Nao abortar a leitura
  // atual a cada aviso: concluir e fazer no maximo UMA leitura adicional.
  const reload = useCallback(() => {
    if (pending.current) queued.current = true;
    else setNonce((n) => n + 1);
  }, []);
  return { state, reload, lastSuccessAt, lastError };
}

/**
 * `true` assim que a PRIMEIRA carga concluiu com sucesso (há `data`),
 * inclusive enquanto um refetch posterior está em `loading` ou falhou em
 * `error` — porque o `data` anterior é preservado nesses dois casos.
 *
 * Use isto (e não `status === "success"`) para decidir se o auto-refresh deve
 * estar ativo. Motivo: ligar o polling por `status === "success"` cria um
 * deadlock — um único poll falho leva `status` a `"error"`, o intervalo é
 * desligado, e religar exigiria um novo `"success"` que só um novo fetch
 * produz. A tela congelaria nos últimos valores em silêncio. Chavear por
 * "já carregou alguma vez" mantém o polling vivo através de erros transitórios
 * (restart da API, blip de rede) e a tela se recupera sozinha no próximo tick.
 *
 * Continua `false` antes da 1ª resposta, preservando a intenção original de
 * não deixar o intervalo abortar o fetch inicial em uma stack lenta.
 */
export function hasLoadedOnce<T>(state: AsyncState<T>): boolean {
  return state.data !== null;
}

/**
 * Comparação estrutural rasa via `JSON.stringify`. Suficiente para os
 * payloads desta camada (objetos planos vindos da API HTTP, sem ciclos,
 * funções nem `undefined` no caminho relevante). Para 1–10 gateways com
 * dezenas de portas, o custo é sub-milissegundo a cada 5s — bem abaixo
 * do que economiza em re-renders evitados.
 *
 * Se um dia o payload virar grande o bastante para custar, troca por
 * diff por `lastSeenAt` + `measuredAt` por porta.
 */
function structurallyEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}
